import { useContext, useState } from 'react';
import cx from 'classnames';
import Image from 'next/image';
import { useTranslations } from 'next-intl';
import Toast from '@/components/Toast';
import { MAX_RELATED_DEVICES } from '@/constants';
import { IDeviceCategory, IDevice } from '@/api/device';
import Styles from './mIndex.module.scss';
import { SafeAreaCtx } from '@/utils/pageEntry';

const emptyImage = require('@/assets/icon/hardware-empty-icon.png');

export interface IDeviceModal {
  selections?: IDevice[];
  deviceCategories?: IDeviceCategory[];
  onCancel: () => void;
  onChange: (devices: IDevice[]) => void;
}

const DeviceModal = ({
  selections = [],
  deviceCategories = [],
  onCancel,
  onChange,
}: IDeviceModal) => {
  const [selectedDevices, setSelectedDevices] = useState(selections);
  const [selectedCategory, setSelectedCategory] = useState(deviceCategories[0] || {});
  const t = useTranslations();

  const handleCancel = () => {
    onCancel();
  };

  const handleSubmit = () => {
    onChange(selectedDevices);
  };

  const handleChangeCategory = (category: IDeviceCategory) => {
    setSelectedCategory(category);
  };

  const handleSelectDevice = (item: IDevice) => {
    const isExist = isDeviceExist(item.key);
    if (!isExist && selectedDevices.length >= MAX_RELATED_DEVICES) {
      Toast.show({
        content: t('error_device_maximum', { maximum: MAX_RELATED_DEVICES }),
      });
      return;
    }

    setSelectedDevices(
      isExist
        ? selectedDevices.filter(({ key }) => key !== item.key)
        : [...selectedDevices, item]
    );
  };

  const renderEmpty = (id: number) => (
    <div key={id} className={Styles.deviceEmpty}>
      <Image
        src={emptyImage}
        alt="empty-device"
        width={100}
        height={100}
        loader={({ src }) => src}
      />
      <p className={Styles.emptyText}>{t('tips_my_devices_empty')}</p>
    </div>
  );

  const isDeviceExist = (deviceKey: string) => selectedDevices.filter(({ key }) => key === deviceKey).length > 0;

  const isDisabledSubmit = !selectedDevices.length;

  const { safeAreaHeight } = useContext(SafeAreaCtx);

  return (
    <div className={Styles.root}>
      <div className={Styles.wrapper}>
        <div className={Styles.header}>
          <div className={Styles.cancel} onClick={handleCancel} />
          <div className={Styles.title}>{t('thread_add_device')}</div>
          <div
            className={cx(Styles.submit, { [Styles.disabled]: isDisabledSubmit })}
            onClick={isDisabledSubmit ? undefined : handleSubmit}
          >
            {t('btn_add')}
          </div>
        </div>
        <div className={Styles.devicesWrapper} style={{
            [ '--safe-area-height' as string]: `${safeAreaHeight}px`
          }}>
          <div className={Styles.categoriesWrapper}>
            <div className={cx(Styles.categories, { [Styles.scroll]: deviceCategories.length > 4 })}>
              {
                deviceCategories.map((category) => {
                  const { id, name } = category;
                  return (
                    <div
                      key={id}
                      className={cx(Styles.category, { [Styles.selected]: category.id === selectedCategory.id })}
                      onClick={() => handleChangeCategory(category)}
                    >
                      { name }
                    </div>
                  );
                })
              }
            </div>
          </div>
          <div className={Styles.series}>
            {
              selectedCategory.series.map(({ id, name , devices}) => id == 0 && devices.length < 1
                ? renderEmpty(id)
                : (
                  <div key={id} className={Styles.item}>
                    <div className={Styles.label}>{name}</div>
                    <div className={Styles.devices}>
                      {
                        devices.map(({ key, name, device }) => (
                          <div
                            key={key}
                            className={cx(Styles.device, { [Styles.selected]: isDeviceExist(key) })}
                            onClick={() => handleSelectDevice({ key, name, device })}
                          >
                            { name }
                          </div>
                        ))
                      }
                    </div>
                  </div>
                )
              )
            }
          </div>
        </div>
      </div>
    </div>
  );
};

export default DeviceModal;
